/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * MovingWorlds API
 * OpenAPI spec version: 0.1.0
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  BusinessChallengeSuggestionRequest,
  CreateProjectFromSuggestionResponse,
  GetProjectSuggestionResponse,
  HTTPValidationError,
  ProjectSuggestionResponse,
  SendProjectSuggestEmailParams,
} from './platformapi.schemas';

import generateBusinessChallengeSuggestionMutator from '../custom-axios-instance';
import generateOrgProjectSuggestionsMutator from '../custom-axios-instance';
import createProjectFromSuggestionMutator from '../custom-axios-instance';
import getProjectSuggestionsMutator from '../custom-axios-instance';
import sendProjectSuggestEmailMutator from '../custom-axios-instance';
import getProjectSuggestionMutator from '../custom-axios-instance';

/**
 * Generate a project suggestion based on a specific business challenge. Used currently from user dashboards to create project recommendations from their business challenge card.
 * @summary Generate Business Challenge Suggestion
 */
export const generateBusinessChallengeSuggestion = (
  businessChallengeSuggestionRequest: BusinessChallengeSuggestionRequest,
  signal?: AbortSignal,
) => {
  return generateBusinessChallengeSuggestionMutator<unknown>({
    url: `${import.meta.env['VITE_API_HOST']}/project-suggestions/create-from-business-challenge`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: businessChallengeSuggestionRequest,
    signal,
  });
};

export const getGenerateBusinessChallengeSuggestionMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateBusinessChallengeSuggestion>>,
    TError,
    { data: BusinessChallengeSuggestionRequest },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateBusinessChallengeSuggestion>>,
  TError,
  { data: BusinessChallengeSuggestionRequest },
  TContext
> => {
  const mutationKey = ['generateBusinessChallengeSuggestion'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateBusinessChallengeSuggestion>>,
    { data: BusinessChallengeSuggestionRequest }
  > = (props) => {
    const { data } = props ?? {};

    return generateBusinessChallengeSuggestion(data);
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateBusinessChallengeSuggestionMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateBusinessChallengeSuggestion>>
>;
export type GenerateBusinessChallengeSuggestionMutationBody = BusinessChallengeSuggestionRequest;
export type GenerateBusinessChallengeSuggestionMutationError = void | HTTPValidationError;

/**
 * @summary Generate Business Challenge Suggestion
 */
export const useGenerateBusinessChallengeSuggestion = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateBusinessChallengeSuggestion>>,
      TError,
      { data: BusinessChallengeSuggestionRequest },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof generateBusinessChallengeSuggestion>>,
  TError,
  { data: BusinessChallengeSuggestionRequest },
  TContext
> => {
  const mutationOptions = getGenerateBusinessChallengeSuggestionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Generate project suggestions for a given organization. This endpoint can be used to generate new suggestions for an organization, or to regenerate suggestions if the organization's details or business challenges have been updated.
 * @summary Generate Org Project Suggestions
 */
export const generateOrgProjectSuggestions = (organizationId: number, signal?: AbortSignal) => {
  return generateOrgProjectSuggestionsMutator<unknown>({
    url: `${import.meta.env['VITE_API_HOST']}/project-suggestions/create-project-suggestions/${organizationId}`,
    method: 'POST',
    signal,
  });
};

export const getGenerateOrgProjectSuggestionsMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof generateOrgProjectSuggestions>>,
    TError,
    { organizationId: number },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof generateOrgProjectSuggestions>>,
  TError,
  { organizationId: number },
  TContext
> => {
  const mutationKey = ['generateOrgProjectSuggestions'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof generateOrgProjectSuggestions>>,
    { organizationId: number }
  > = (props) => {
    const { organizationId } = props ?? {};

    return generateOrgProjectSuggestions(organizationId);
  };

  return { mutationFn, ...mutationOptions };
};

export type GenerateOrgProjectSuggestionsMutationResult = NonNullable<
  Awaited<ReturnType<typeof generateOrgProjectSuggestions>>
>;

export type GenerateOrgProjectSuggestionsMutationError = void | HTTPValidationError;

/**
 * @summary Generate Org Project Suggestions
 */
export const useGenerateOrgProjectSuggestions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof generateOrgProjectSuggestions>>,
      TError,
      { organizationId: number },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof generateOrgProjectSuggestions>>,
  TError,
  { organizationId: number },
  TContext
> => {
  const mutationOptions = getGenerateOrgProjectSuggestionsMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Creates a project from a given project suggestion. Currently utilized by admin endpoint to use existing db suggested project data to populate various project tables. Result is a project viewable in admin and user dashboards in 'Review' stage.
 * @summary Create Project From Suggestion
 */
export const createProjectFromSuggestion = (projectSuggestionId: number, signal?: AbortSignal) => {
  return createProjectFromSuggestionMutator<CreateProjectFromSuggestionResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/project-suggestions/create-project/${projectSuggestionId}`,
    method: 'POST',
    signal,
  });
};

export const getCreateProjectFromSuggestionMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createProjectFromSuggestion>>,
    TError,
    { projectSuggestionId: number },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createProjectFromSuggestion>>,
  TError,
  { projectSuggestionId: number },
  TContext
> => {
  const mutationKey = ['createProjectFromSuggestion'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createProjectFromSuggestion>>,
    { projectSuggestionId: number }
  > = (props) => {
    const { projectSuggestionId } = props ?? {};

    return createProjectFromSuggestion(projectSuggestionId);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateProjectFromSuggestionMutationResult = NonNullable<
  Awaited<ReturnType<typeof createProjectFromSuggestion>>
>;

export type CreateProjectFromSuggestionMutationError = void | HTTPValidationError;

/**
 * @summary Create Project From Suggestion
 */
export const useCreateProjectFromSuggestion = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createProjectFromSuggestion>>,
      TError,
      { projectSuggestionId: number },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createProjectFromSuggestion>>,
  TError,
  { projectSuggestionId: number },
  TContext
> => {
  const mutationOptions = getCreateProjectFromSuggestionMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * Retrieve 3 most recent project suggestions for a given organization.
 * @summary Get Project Suggestions
 */
export const getProjectSuggestions = (organizationId: number, signal?: AbortSignal) => {
  return getProjectSuggestionsMutator<ProjectSuggestionResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/project-suggestions/project-suggestions/${organizationId}`,
    method: 'GET',
    signal,
  });
};

export const getGetProjectSuggestionsQueryKey = (organizationId?: number) => {
  return [
    `${import.meta.env['VITE_API_HOST']}/project-suggestions/project-suggestions/${organizationId}`,
  ] as const;
};

export const getGetProjectSuggestionsQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectSuggestions>>,
  TError = void | HTTPValidationError,
>(
  organizationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestions>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProjectSuggestionsQueryKey(organizationId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectSuggestions>>> = ({ signal }) =>
    getProjectSuggestions(organizationId, signal);

  return { queryKey, queryFn, enabled: !!organizationId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProjectSuggestions>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProjectSuggestionsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProjectSuggestions>>
>;
export type GetProjectSuggestionsQueryError = void | HTTPValidationError;

export function useGetProjectSuggestions<
  TData = Awaited<ReturnType<typeof getProjectSuggestions>>,
  TError = void | HTTPValidationError,
>(
  organizationId: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestions>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectSuggestions>>,
          TError,
          Awaited<ReturnType<typeof getProjectSuggestions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectSuggestions<
  TData = Awaited<ReturnType<typeof getProjectSuggestions>>,
  TError = void | HTTPValidationError,
>(
  organizationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestions>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectSuggestions>>,
          TError,
          Awaited<ReturnType<typeof getProjectSuggestions>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectSuggestions<
  TData = Awaited<ReturnType<typeof getProjectSuggestions>>,
  TError = void | HTTPValidationError,
>(
  organizationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Project Suggestions
 */

export function useGetProjectSuggestions<
  TData = Awaited<ReturnType<typeof getProjectSuggestions>>,
  TError = void | HTTPValidationError,
>(
  organizationId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestions>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProjectSuggestionsQueryOptions(organizationId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Sends the project suggestion email template to the most recent org rep associated with the suggested project organization
 * @summary Send Project Suggest Email
 */
export const sendProjectSuggestEmail = (
  params: SendProjectSuggestEmailParams,
  signal?: AbortSignal,
) => {
  return sendProjectSuggestEmailMutator<unknown>({
    url: `${import.meta.env['VITE_API_HOST']}/project-suggestions/send-project-suggest-email`,
    method: 'POST',
    params,
    signal,
  });
};

export const getSendProjectSuggestEmailMutationOptions = <
  TError = void | HTTPValidationError,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sendProjectSuggestEmail>>,
    TError,
    { params: SendProjectSuggestEmailParams },
    TContext
  >;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sendProjectSuggestEmail>>,
  TError,
  { params: SendProjectSuggestEmailParams },
  TContext
> => {
  const mutationKey = ['sendProjectSuggestEmail'];
  const { mutation: mutationOptions } =
    options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
        options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sendProjectSuggestEmail>>,
    { params: SendProjectSuggestEmailParams }
  > = (props) => {
    const { params } = props ?? {};

    return sendProjectSuggestEmail(params);
  };

  return { mutationFn, ...mutationOptions };
};

export type SendProjectSuggestEmailMutationResult = NonNullable<
  Awaited<ReturnType<typeof sendProjectSuggestEmail>>
>;

export type SendProjectSuggestEmailMutationError = void | HTTPValidationError;

/**
 * @summary Send Project Suggest Email
 */
export const useSendProjectSuggestEmail = <TError = void | HTTPValidationError, TContext = unknown>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof sendProjectSuggestEmail>>,
      TError,
      { params: SendProjectSuggestEmailParams },
      TContext
    >;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof sendProjectSuggestEmail>>,
  TError,
  { params: SendProjectSuggestEmailParams },
  TContext
> => {
  const mutationOptions = getSendProjectSuggestEmailMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
/**
 * @summary Get Project Suggestion
 */
export const getProjectSuggestion = (projectSuggestionId: number, signal?: AbortSignal) => {
  return getProjectSuggestionMutator<GetProjectSuggestionResponse>({
    url: `${import.meta.env['VITE_API_HOST']}/project-suggestions/${projectSuggestionId}`,
    method: 'GET',
    signal,
  });
};

export const getGetProjectSuggestionQueryKey = (projectSuggestionId?: number) => {
  return [
    `${import.meta.env['VITE_API_HOST']}/project-suggestions/${projectSuggestionId}`,
  ] as const;
};

export const getGetProjectSuggestionQueryOptions = <
  TData = Awaited<ReturnType<typeof getProjectSuggestion>>,
  TError = void | HTTPValidationError,
>(
  projectSuggestionId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestion>>, TError, TData>
    >;
  },
) => {
  const { query: queryOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetProjectSuggestionQueryKey(projectSuggestionId);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getProjectSuggestion>>> = ({ signal }) =>
    getProjectSuggestion(projectSuggestionId, signal);

  return { queryKey, queryFn, enabled: !!projectSuggestionId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getProjectSuggestion>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetProjectSuggestionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getProjectSuggestion>>
>;
export type GetProjectSuggestionQueryError = void | HTTPValidationError;

export function useGetProjectSuggestion<
  TData = Awaited<ReturnType<typeof getProjectSuggestion>>,
  TError = void | HTTPValidationError,
>(
  projectSuggestionId: number,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestion>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectSuggestion>>,
          TError,
          Awaited<ReturnType<typeof getProjectSuggestion>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectSuggestion<
  TData = Awaited<ReturnType<typeof getProjectSuggestion>>,
  TError = void | HTTPValidationError,
>(
  projectSuggestionId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestion>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getProjectSuggestion>>,
          TError,
          Awaited<ReturnType<typeof getProjectSuggestion>>
        >,
        'initialData'
      >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetProjectSuggestion<
  TData = Awaited<ReturnType<typeof getProjectSuggestion>>,
  TError = void | HTTPValidationError,
>(
  projectSuggestionId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestion>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get Project Suggestion
 */

export function useGetProjectSuggestion<
  TData = Awaited<ReturnType<typeof getProjectSuggestion>>,
  TError = void | HTTPValidationError,
>(
  projectSuggestionId: number,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getProjectSuggestion>>, TError, TData>
    >;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetProjectSuggestionQueryOptions(projectSuggestionId, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
